import type { BadgeProps } from '@ctw/shared/components/badge';
import { Badge } from '@ctw/shared/components/badge';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircleCheck as faCircleCheckSolid,
  faExclamationCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { isEmpty } from 'lodash-es';
import type { JSX, ReactNode } from 'react';

export type WritebackStatuses = Record<string, WritebackStatus | undefined>;

export type WritebackAction = 'add' | 'dismiss-bulk' | 'dismiss-single' | 'restore';

export type ResourceTitleColumnProps = {
  title?: string | JSX.Element;
  subTitle?: string | JSX.Element;
  capitalizeTitle?: boolean;
  className?: ClassName;
  titleClassName?: ClassName;
  renderIcon?: ReactNode;
  badges?: Array<Omit<BadgeProps, 'size'>>;
};

export const ResourceTitleColumn = ({
  title,
  subTitle,
  className,
  titleClassName,
  badges = [],
  renderIcon, // Add the renderIcon prop to be used within the component
}: ResourceTitleColumnProps) => (
  <div className={twx(className)}>
    <div className={tw`flex gap-2`}>
      {renderIcon ? <div className={tw`flex flex-nowrap`}>{renderIcon}</div> : null}
      <div
        className={twx(
          'w-full font-medium group-hover:underline',
          {
            'pb-1.5': !isEmpty(subTitle),
          },
          titleClassName,
        )}
      >
        <div className={tw`pb-1`}>
          {badges.map((badgeProps) => (
            <Badge key={badgeProps.content} {...badgeProps} size="sm" />
          ))}
        </div>
        {title}
      </div>
    </div>
    {subTitle ? <div>{subTitle}</div> : null}
  </div>
);

// The function to render the FontAwesome icon used for ownedByBuilder
export const RenderSyncedWithRecordIcon = (enable = true, isNew = false) =>
  enable && (
    <Tooltip id="synced-with-record-icon" content="Synced with record">
      <FontAwesomeIcon
        className={tw`${isNew ? 'text-success-main' : 'text-content-subtle'} size-[14px]`}
        icon={isNew ? faCircleCheckSolid : faCircleCheck}
      />
    </Tooltip>
  );

export type WritebackStatus = {
  state: WritebackState;
  message?: string;
};

export type WritebackState = (typeof writebackStatuses)[number];

const writebackStatuses = [
  'unknown',
  'not-known-to-builder',
  'in-progress',
  'error',
  'known-to-builder-new',
  'known-to-builder',
  'bulk-dismissed-new',
  'dismissed',
] as const;

export function isSelectableWritebackStatus(status: WritebackStatus | undefined): boolean {
  const includedStatuses: Array<WritebackState> = ['not-known-to-builder', 'unknown'];
  return !status || includedStatuses.includes(status.state);
}

export type Selection = {
  isSelected: boolean;
  changeState: (isBeingSelected: boolean) => void;
};

// The icon on the left of the record. Has several options indicating different things relating to the record's ownership by this builder.
export const RenderWritebackStatusIcon = (
  writebackStatus: WritebackStatus,
  bulkActionSelectState?: Selection,
  isBulkWritebackEnabled?: boolean,
) => {
  const { state: writebackState, message = 'There was an error adding this record to the chart.' } =
    writebackStatus;

  switch (writebackState) {
    case 'known-to-builder-new':
      return RenderSyncedWithRecordIcon(true, true);

    case 'in-progress':
      return <LoadingSpinner iconClass={tw`h-[14px] w-[14px]`} />;

    case 'error':
      return (
        <Tooltip id="resource-title-column-error" content={message}>
          <FontAwesomeIcon
            className={tw`h-[14px] w-[14px] text-error-main`}
            icon={faExclamationCircle}
          />
        </Tooltip>
      );

    case 'bulk-dismissed-new':
      return (
        <Tooltip id="resource-title-column-bulk-dismissed-new" content="Successfully dismissed">
          <FontAwesomeIcon
            className={tw`h-[14px] w-[14px] text-content-subtle`}
            icon={faCircleCheckSolid}
          />
        </Tooltip>
      );

    case 'dismissed':
      return isBulkWritebackEnabled ? (
        <input type="checkbox" className={tw`w-4`} checked={false} disabled={true} />
      ) : undefined;

    case 'known-to-builder':
      return RenderSyncedWithRecordIcon(true);

    default:
      if (isBulkWritebackEnabled && bulkActionSelectState) {
        const { isSelected, changeState } = bulkActionSelectState;
        return <BulkAddSelectBox isSelected={isSelected} changeState={changeState} />;
      }
  }
  return undefined;
};

export const BulkAddSelectBox = ({ isSelected, changeState }: Selection) => (
  <input
    type="checkbox"
    className={tw`w-4`}
    onClick={(event) => {
      event.stopPropagation();
      changeState(!isSelected);
    }}
    checked={isSelected}
    onChange={(e) => {
      e.stopPropagation();
    }}
  />
);

// The function to render the FontAwesome icon used for info badge
export const RenderInfoBadge = (
  infoBadge?: string,
  infoBadgeClassName?: string,
  toolTipText?: string,
) =>
  infoBadge && (
    <div data-testid="indicator" className={twx(infoBadgeClassName)}>
      {toolTipText ? (
        <div className={tw`self-start`}>
          <Tooltip id="resource-title-column-info" content={toolTipText}>
            <Badge variant="info" content={infoBadge} />
          </Tooltip>
        </div>
      ) : (
        <Badge variant="info" content={infoBadge} />
      )}
    </div>
  );
